import {
    useCallback, useEffect, useRef, useState
} from 'react';
import framework from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import SignInEN from 'pages/Auth/locales/SignIn_en_US';
import SignInFR from 'pages/Auth/locales/SignIn_fr_CA';
import {
    colors, fontSizes, fontWeights, space
} from 'style/config';
import TextInput from 'components/Inputs/TextInput/TextInput';
import Button from 'components/Button/Button.f';
import buttonStyles from 'components/Button/styles';
import Divider from 'components/Divider/Divider.f';
import Icon from 'components/Icon/Icon.f';
import Link from 'components/Link/Link.f';
import store from 'Store';
import Actions from 'Actions';
import FormValidator from 'utils/FormValidator';
import normalize from 'utils/credentialNormalizer';
import urlUtils from 'utils/Url';
import getCredentialLookupError from 'pages/Auth/SignIn/credentialLookupErrorMap';
import useCredentialLookup from 'pages/Auth/SignIn/hooks/useCredentialLookup';

const { wrapFunctionalComponent } = framework;

// No-op fallback so the screen can render standalone (e.g. in isolation tests)
// without a navigation handler; the real goTo is owned by the SignIn page.
const noop = () => {};
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(SignInEN, SignInFR);

const REGISTER_ROUTE = '/register';
// ACS-4362 (VQA, Figma 1232-1309428): the security-locked account message links to
// the password-reset flow (matches AuthorizationScreen's Forgot Password affordance).
const RESET_PASSWORD_ROUTE = '/reset-password';

// credentialType is derived at THIS call site from the normalizer's `type`
// (credentialNormalizer keeps its committed 3-outcome shape unchanged).
const CREDENTIAL_TYPE_BY_NORMALIZER_TYPE = {
    email: 'EMAIL',
    phone: 'PHONE'
};

// SIM-9 / ACS-4133 (Figma 1466-19076): the phone-disambiguation step reuses THIS
// screen in a copy-only 'email-confirmation' mode. Default 'credential' preserves
// the original SIM-2 behavior.
const MODE_EMAIL_CONFIRMATION = 'email-confirmation';

// ACS-4388 (VQA, SIM-7 AC-AUTH-EDIT): format a restored credential for display when
// the field is pre-filled on "Edit". An all-digits value is a bare NANP phone
// (authContext stores enteredCredential as normalized.value → 10 bare digits for a
// phone); re-mask it to (xxx) xxx-xxxx to match handleChange's live-format display.
// An email (or any non-digits value) is shown verbatim. Empty stays empty.
const formatInitialCredential = value => {
    if (!value) {
        return '';
    }

    const isDigitsOnly = /^\d+$/.test(value);

    if (isDigitsOnly && value.length <= 10) {
        return FormValidator.getFormattedPhoneNumber(value) || value;
    }

    return value;
};

/**
 * CredentialEntryScreen (SIM-2 / ACS-4126, SUB-TASK-2b).
 *
 * One free-text field (name='credential', BR-045/TD-018) that accepts an email
 * OR a 10-digit NANP phone. While the content is all-digits the value is live
 * auto-formatted to (xxx) xxx-xxxx with NO hard cap; the mask reverts to plain
 * text the moment an email character appears so email entry is never fought.
 *
 * Validation runs ON SUBMIT ONLY (via credentialNormalizer). Invalid input
 * short-circuits to a SINGLE generic AC-018 error with no email-vs-phone leak
 * (BR-020) and makes NO API call. Valid input derives credentialType
 * ('EMAIL'/'PHONE') from the normalizer result and calls useCredentialLookup
 * against the real OI-4 `authenticationMethods` contract, then branches:
 *   - requiresEmailConfirmation -> disambiguation (SIM-9)
 *   - securityToken (single account) -> authorization (carrying securityToken)
 *   - ERR_PHONE_NOT_FOUND / ERR_EMAIL_NOT_FOUND -> not-found: typical input-field
 *     error BELOW the field (backend errorMessage + locale fallback), no leak
 *   - ERR_ACCOUNT_CLOSED -> reactivate: plain-text message ABOVE the field
 *     (backend errorMessage + locale fallback), Figma 753-95460
 *   - ERR_ACCOUNT_SYS_LOGIN -> locked: message ABOVE the field with an inline
 *     "Reset your Password" link to /reset-password, Figma 1232-1309428
 *   - ERR_400004 (store-registered) -> "Check Your Email" verification modal
 *     when the credential was an email; inline generic error for a phone
 *   - any other code / 5xx -> AC-018 generic backend error
 * The errorCode -> presentation mapping lives in credentialLookupErrorMap so the
 * branching stays a data-driven lookup rather than a growing switch.
 * Double-submit is prevented (BR-024) via the in-flight disabled/loading button.
 *
 * Navigation is driven by the SINGLE useAuthFlow() state machine instance owned
 * by the SignIn page (pages/Auth/SignIn.f.jsx); its `goTo` is passed in as a
 * prop. This screen must NOT call useAuthFlow() itself — a second instance would
 * be independent state and goTo() here would not update the screen the page shell
 * renders against.
 *
 * @param {object} props
 * @param {(screen: string) => void} [props.goTo] - advance the shared auth-flow
 *   state machine to another screen (defaults to a no-op for standalone render).
 * @param {(context: object) => void} [props.setAuthContext] - carry the lookup
 *   result forward into the shared authContext BEFORE advancing to authorization
 *   so AuthorizationScreen/OtpEntryScreen have their inputs without a re-lookup
 *   (SIM-7 / ACS-4131, F-002). Defaults to a no-op for standalone render.
 * @param {'credential'|'email-confirmation'} [props.mode] - copy-only variant
 *   (SIM-9 / ACS-4133, Figma 1466-19076). In 'email-confirmation' mode the subtitle
 *   and field label switch to the disambiguation copy and submit is forced to
 *   credentialType EMAIL; default 'credential' is the original SIM-2 behavior.
 * @param {string} [props.initialCredential] - ACS-4388 (VQA, SIM-7 AC-AUTH-EDIT):
 *   the value the customer previously entered, carried back when they click "Edit"
 *   on the Authorization/OTP step so the field lands PRE-FILLED and editable (no
 *   lookup re-issued). Sourced from the in-session authContext.enteredCredential
 *   (the raw value is PII and is never persisted — this is the in-memory Edit path,
 *   BR-021). A phone (bare digits) is re-formatted to (xxx) xxx-xxxx for display.
 * @param {boolean} [props.autoSubmit] - SIM-17 (ACS-4141) DEFER-2b: when true AND a
 *   pre-seeded initialCredential is present, fire the existing handleSubmit ONCE on mount
 *   so a customer redirected from account-closure re-auth lands directly on the
 *   authorization/OTP step without clicking Continue. Default false (normal manual entry).
 */
const CredentialEntryScreen = ({
    goTo = noop, setAuthContext = noop, mode = 'credential', initialCredential = '', autoSubmit = false
}) => {
    const isEmailConfirmation = mode === MODE_EMAIL_CONFIRMATION;
    const { lookup, isLoading } = useCredentialLookup();
    // ACS-4388: seed the field with the prior entry so "Edit" returns pre-filled
    // (AC-AUTH-EDIT). Re-format an all-digits phone to match the live-format display
    // (handleChange), so the restored value looks exactly as the user left it.
    // ACS-4437 (AC-4): a registration-redirect credential is carried via the SAME
    // `initialCredential` prop and paired with `autoSubmit` — unifying with the
    // SIM-17/ACS-4141 auto-submit contract so the two stories share one code path.
    const [credential, setCredential] = useState(() => formatInitialCredential(initialCredential));
    // ACS-4362 (VQA) — three positionally-distinct error surfaces:
    //  - inputError: the "typical input-field error" (Figma 288-97648 / 1308-116056),
    //    rendered BELOW the field via TextInput's native `invalid` + `error` props.
    //    Covers BOTH invalid FORMAT and not-found (ERR_*_NOT_FOUND).
    //  - accountError: account-status/backend errors, rendered ABOVE the field as a
    //    plain-text inline alert (Figma 753-95460 — reactivate copy + AC-018/AC-034).
    //  - locked: security-locked account (ERR_ACCOUNT_SYS_LOGIN) — rendered ABOVE the
    //    field with an inline "Reset your Password" LINK (Figma 1232-1309428). A
    //    separate flag because its message contains an anchor (accountError is text).
    const [inputError, setInputError] = useState(null);
    const [accountError, setAccountError] = useState(null);
    const [locked, setLocked] = useState(false);
    const isSubmittingRef = useRef(false);
    // ACS-4437 / SIM-17 (ACS-4141): guards the auto-submit effect below against firing
    // twice — `autoSubmit` + a seeded `initialCredential` typically arrive AFTER this
    // screen's first render (SIM-2's TEMP dual-screen render mounts it on
    // 'passkey-detection' before the passkey-detection effect resolves and calls goTo
    // with the patch), so the effect is watched, not mount-only.
    const hasAutoSubmittedRef = useRef(false);

    // Live phone auto-format-with-revert. No validation, no error here.
    const handleChange = useCallback(event => {
        const value = event?.target?.value ?? '';
        const stripped = FormValidator.getRawPhoneNumber(value).replace(/\./g, '');
        const isDigitsOnly = stripped.length > 0 && /^\d+$/.test(stripped);

        if (isDigitsOnly) {
            // phone-in-progress: reversible mask, allow >10 (shown as raw digits)
            const display = stripped.length <= 10 ? FormValidator.getFormattedPhoneNumber(stripped) || stripped : stripped;
            setCredential(display);
        } else {
            // an email character appeared -> leave the plain text as typed
            setCredential(value);
        }
    }, []);

    // `submitValue` lets the auto-submit effect below submit the seeded value directly,
    // instead of racing the `credential` state update. A manual Button onClick passes a
    // SyntheticEvent here (not a string), so it safely falls through to the `credential`
    // state as before. (Unified ACS-4437 registration-redirect + SIM-17/ACS-4141 auto-submit.)
    const handleSubmit = useCallback(
        async submitValue => {
            if (isSubmittingRef.current) {
                return;
            }

            isSubmittingRef.current = true;
            setInputError(null);
            setAccountError(null);
            setLocked(false);

            const rawCredential = typeof submitValue === 'string' ? submitValue : credential;
            const normalized = normalize(rawCredential);

            // Email-confirmation mode (SIM-9, Figma 1466-19076): the disambiguation step
            // resolves an ambiguous phone by EMAIL, so the entry MUST be an actual email.
            // A phone/invalid value is rejected inline with no API call (would otherwise
            // send a non-email value tagged as EMAIL to the lookup).
            if (isEmailConfirmation ? normalized.type !== 'email' : normalized.type === 'invalid') {
                // ACS-4362: invalid FORMAT is the typical input-field error — shown BELOW
                // the field via TextInput's native invalid state (Figma 288-97648).
                setInputError(getText('invalidCredential'));
                isSubmittingRef.current = false;

                return;
            }

            // In email-confirmation mode the credential is a validated email, so submit is
            // FORCED to EMAIL; otherwise map the normalizer type to the credential type.
            const credentialType = isEmailConfirmation ? 'EMAIL' : CREDENTIAL_TYPE_BY_NORMALIZER_TYPE[normalized.type];
            const isEmail = isEmailConfirmation || normalized.type === 'email';
            const response = await lookup(normalized.value, credentialType);

            if (response.error) {
                // Map the backend error code to its presentation (credentialLookupErrorMap).
                const { kind } = getCredentialLookupError(response.errorCode);

                switch (kind) {
                    case 'notFound':
                        // ACS-4362 (VQA, Figma 1308-116056): not-found is the typical
                        // input-field error — red field + in-field icon + message BELOW,
                        // NO inline link (Create Account is the button at the bottom).
                        // Show the backend errorMessage ("No account found. Please try
                        // again or create an account."), falling back to the locale copy
                        // if the backend omits it. BR-020: identical for phone and email
                        // so the message never leaks which was read.
                        setInputError(response.errorMessage || getText('notFound'));

                        break;
                    case 'reactivate':
                        // ACS-4362 (VQA, Figma 753-95460): closed/deactivated account —
                        // show the reactivate copy ABOVE the field as plain red text (no
                        // link / chat CTA). The backend errorMessage carries the live copy
                        // ("...(1-877-737-4672) Or Chat with our agents."); the locale
                        // string is the client fallback when the backend omits it.
                        setAccountError(response.errorMessage || getText('reactivateAccount'));

                        break;
                    case 'locked':
                        // ACS-4362 (VQA, Figma 1232-1309428): security-locked account —
                        // red message ABOVE the field with an inline "Reset your Password"
                        // LINK to /reset-password. Client locale copy is used (the anchor
                        // can't be applied to the raw backend errorMessage), so unlike
                        // reactivate this does NOT render response.errorMessage verbatim.
                        setLocked(true);

                        break;
                    case 'modalStoreRegistered':
                        // ERR_400004 store-registered: open the "Check Your Email"
                        // verification modal ONLY when the entered credential was an
                        // email (the modal shows that address). For a phone-entered
                        // credential there is no email to show, so fall back to the
                        // inline generic error.
                        if (isEmail) {
                            store.dispatch(Actions.showCheckYourEmailModal({ isOpen: true, email: normalized.value }));
                        } else {
                            setAccountError(getText('genericError'));
                        }

                        break;
                    default:
                        // 'generic' + any unrecognized code / 5xx (AC-018 / AC-034).
                        setAccountError(getText('genericError'));
                }
            } else if (response.requiresEmailConfirmation) {
                goTo('disambiguation');
            } else if (response.securityToken) {
                // F-002: carry the lookup result already in hand into the shared
                // authContext BEFORE advancing, so AuthorizationScreen/OtpEntryScreen
                // render against real inputs and no second lookup is issued. Sourced
                // entirely from `response` + the entered credential — nothing fabricated.
                setAuthContext({
                    securityToken: response.securityToken,
                    availableAuthMethods: response.availableAuthMethods,
                    phoneLast4Digits: response.phoneLast4Digits,
                    maskedEmail: response.maskedEmail,
                    enteredCredential: normalized.value,
                    credentialType
                });
                goTo('authorization');
            } else {
                setAccountError(getText('genericError'));
            }

            isSubmittingRef.current = false;
        },
        [credential, lookup, goTo, setAuthContext, isEmailConfirmation]
    );

    // ACS-4437 (AC-4) + SIM-17/ACS-4141 (DEFER-2b): registration-redirect / re-auth
    // credential auto-submit — UNIFIED single atomic effect (shared with ACS-4141 to
    // avoid a conflicting second implementation). `autoSubmit` + a pre-seeded
    // `initialCredential` typically arrive on a render AFTER this screen first mounted
    // (SIM-2 TEMP dual-screen render mounts it on 'passkey-detection' before the
    // passkey-detection effect resolves and calls goTo with the patch), so the effect
    // is watched (not mount-only []). `hasAutoSubmittedRef` fires it exactly once: seed
    // the field to the display value, then call the existing handleSubmit with that value
    // (so the failure path renders the SAME inputError/accountError/locked UX as a manual
    // submit, and the submit never races the setCredential state update). Empty seed is a
    // no-op.
    useEffect(() => {
        if (autoSubmit && initialCredential && !hasAutoSubmittedRef.current) {
            hasAutoSubmittedRef.current = true;
            const seeded = formatInitialCredential(initialCredential);

            setCredential(seeded);
            handleSubmit(seeded);
        }
    }, [autoSubmit, initialCredential, handleSubmit]);

    // ACS-4362 (VQA, Figma 1232-1309428): the locked-account "Reset your Password"
    // link routes to the password-reset flow. Prevent Link's default anchor behaviour
    // and use the SPA-safe Url.redirectTo (mirrors AuthorizationScreen.handleForgotPassword).
    const handleResetPassword = event => {
        event?.preventDefault?.();
        urlUtils.redirectTo(RESET_PASSWORD_ROUTE);
    };

    return (
        <div>
            {/* Subtitle: default = FREE-shipping (Figma 270-84077, middle span bold);
                email-confirmation = the generic disambiguation prompt (Figma 1466-19076,
                no account-count leak — BR-002/003). */}
            {isEmailConfirmation ? (
                <p css={styles.subtitle}>{getText('emailConfirmSubtitle')}</p>
            ) : (
                <p css={styles.subtitle}>
                    {getText('subtitlePrefix')}
                    <strong>{getText('subtitleBold')}</strong>
                    {getText('subtitleSuffix')}
                </p>
            )}
            {/* ACS-4362 (VQA, Figma 753-95460): account-status / backend errors render
                ABOVE the field (between the subtitle and the input) — the reactivate copy
                (ERR_ACCOUNT_CLOSED) and the AC-018/AC-034 generic backend error. Distinct
                from the invalid-FORMAT / not-found errors, which are the typical input-field
                error rendered below the field via TextInput's native invalid state. */}
            {accountError && (
                <div
                    role='alert'
                    aria-live='assertive'
                    css={styles.accountError}
                >
                    <Icon
                        name='alert'
                        size={16}
                        css={styles.errorIcon}
                    />
                    <span>{accountError}</span>
                </div>
            )}
            {/* ACS-4362 (VQA, Figma 1232-1309428): security-locked account (ERR_ACCOUNT_SYS_LOGIN)
                — same position/style as accountError but the copy carries an inline
                "Reset your Password" link to /reset-password (SPA-safe redirect). */}
            {locked && (
                <div
                    role='alert'
                    aria-live='assertive'
                    css={styles.accountError}
                >
                    <Icon
                        name='alert'
                        size={16}
                        css={styles.errorIcon}
                    />
                    <span>
                        {getText('lockedAccountPrefix')}
                        <Link
                            href={RESET_PASSWORD_ROUTE}
                            color='error'
                            underline={true}
                            onClick={handleResetPassword}
                        >
                            {getText('lockedAccountResetLink')}
                        </Link>
                        {getText('lockedAccountSuffix')}
                    </span>
                </div>
            )}
            <TextInput
                name='credential'
                label={isEmailConfirmation ? getText('emailConfirmLabel') : getText('credentialLabel')}
                aria-label={isEmailConfirmation ? getText('emailConfirmLabel') : getText('credentialLabel')}
                placeholder={isEmailConfirmation ? getText('emailConfirmLabel') : getText('credentialPlaceholder')}
                autoOff={true}
                value={credential}
                onChange={handleChange}
                invalid={Boolean(inputError)}
                error={inputError}
            />
            <Button
                variant='primary'
                block={true}
                onClick={handleSubmit}
                disabled={isLoading}
                loading={isLoading}
                css={styles.continue}
            >
                {getText('continueButton')}
            </Button>
            <Divider css={styles.divider} />
            <p css={styles.newToSephora}>{getText('newToSephora')}</p>
            {/* Create Account: an <a> to /register visually styled as the
                secondary outlined pill (reuses buttonStyles). Kept as a LINK
                (not a <button>) so only "Continue" carries the button role. */}
            <a
                href={REGISTER_ROUTE}
                css={styles.createAccount}
            >
                {getText('createAccountButton')}
            </a>
        </div>
    );
};

const styles = {
    subtitle: {
        color: colors.black,
        fontSize: fontSizes.base,
        marginTop: 0,
        marginBottom: space[4]
    },
    // ACS-4362 (VQA, Figma 753-95460 / 1232-1309428): account-status / backend error
    // alert shown ABOVE the field (warning icon + error text in colors.error, 12px;
    // role="alert", ADR-002). marginBottom spaces it between the subtitle and the input.
    accountError: {
        display: 'flex',
        gap: space[2],
        alignItems: 'flex-start',
        color: colors.error,
        fontSize: fontSizes.sm,
        marginBottom: space[4]
    },
    errorIcon: {
        flexShrink: 0,
        color: colors.error
    },
    continue: {
        marginTop: space[4]
    },
    divider: {
        marginTop: space[4],
        marginBottom: space[4]
    },
    newToSephora: {
        color: colors.black,
        fontSize: fontSizes.base,
        fontWeight: fontWeights.bold,
        marginTop: 0,
        marginBottom: space[4]
    },
    createAccount: [
        buttonStyles.size(),
        ...buttonStyles.secondary,
        {
            display: 'flex',
            width: '100%'
        }
    ]
};

export default wrapFunctionalComponent(CredentialEntryScreen, 'CredentialEntryScreen');
